import React from 'react';
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  Bus,
  CheckCircle2,
  Code2,
  CreditCard,
  Database,
  GraduationCap,
  MapPin,
  MessageCircle,
  MonitorSmartphone,
  Network,
  QrCode,
  Radio,
  Rocket,
  Server,
  Smartphone,
  Users,
  WalletCards,
  Wifi
} from 'lucide-react';
import { ceo } from '../data/mock';
import geducaImage from '../assets/images/wamberto/geduca-servicos.png';
import wcompassImage from '../assets/images/wamberto/wcompass-apresentacao.png';
import wcomticketImage from '../assets/images/wamberto/wcomticket-compra.png';
import ecopasseImage from '../assets/images/wamberto/ecopasse-app.png';

const wamberto = [
  {
    id: 'geduca',
    number: '01',
    image: geducaImage,
    imageAlt: 'Página real de serviços da plataforma GEDUCA',
    name: 'GEDUCA',
    eyebrow: 'Gestão educacional para municípios.',
    description:
      'Uma plataforma completa que integra os processos da rede municipal de ensino em uma única solução.',
    metrics: [
      { value: '200 mil+', label: 'alunos' },
      { value: 'Centenas', label: 'de escolas' },
      { value: 'Uso diário', label: 'por professores, alunos e secretarias' }
    ],
    features: [
      { label: 'Diário Online', icon: BookOpen },
      { label: 'Matrícula', icon: GraduationCap },
      { label: 'Transporte', icon: Bus },
      { label: 'Merenda', icon: Users },
      { label: 'Educacenso', icon: Database },
      { label: 'BI', icon: BarChart3 }
    ],
    challenge:
      'Modernizar toda a gestão educacional de um município, integrando secretaria, escolas, professores, alunos e responsáveis em uma única plataforma.',
    scale:
      'Mais de 200 mil alunos e professores, centenas  de escolas utilizando diariamente a solução.',
    role:
      'Arquitetura da solução, modelagem do sistema, desenvolvimento, evolução contínua e sustentação da plataforma.',
    result:
      'Uma solução integrada que contempla matrícula, diário online, transporte escolar, merenda, avaliação, Educacenso, BI e portais para professores, alunos e responsáveis.'
  },
  {
    id: 'wcompass',
    number: '02',
    image: wcompassImage,
    imageAlt: 'Página real de apresentação da plataforma WCOMPass',
    name: 'WCOMPass',
    eyebrow: 'Mobilidade inteligente para empresas e condomínios.',
    description:
      'Plataforma de gestão operacional do transporte corporativo com georeferenciamento em tempo real e aplicativos móveis.',
    metrics: [
      { value: 'Tempo real', label: 'rastreamento e operação' },
      { value: 'QR Code', label: 'controle de embarque' },
      { value: 'NFC + BLE', label: 'validação por proximidade' },
      { value: 'Multiempresa', label: 'gestão centralizada' }
    ],
    features: [
      { label: 'Rastreamento', icon: MapPin },
      { label: 'Embarque', icon: QrCode },
      { label: 'NFC e BLE', icon: Radio },
      { label: 'Relatórios', icon: BarChart3 },
      { label: 'Comunicação', icon: Wifi }
    ],
    challenge:
      'Eliminar controles manuais e oferecer uma visão completa da operação de transporte em tempo real.',
    scale:
      'Plataforma concebida para operações de transporte corporativo que precisam centralizar controle, rastreamento, embarque e comunicação em um único ambiente.',
    role:
      'Arquitetura, desenvolvimento da plataforma, aplicativos móveis, integrações e infraestrutura operacional.',
    result:
      'Controle de embarque por QR Code, NFC e BLE, rastreamento em tempo real, aplicativos móveis e portal administrativo integrados.'
  },
  {
    id: 'wcomticket',
    number: '03',
    image: wcomticketImage,
    imageAlt: 'Página real de compra de passagens do WCOMTicket',
    name: 'WCOMTicket',
    eyebrow: 'A nova geração da bilhetagem eletrônica.',
    description:
      'Uma plataforma concebida para acompanhar a transformação dos meios de pagamento.',
    metrics: [
      { value: '100%', label: 'online' },
      { value: 'Múltiplos', label: 'meios de pagamento' },
      { value: 'Integração', label: 'em tempo real' },
      { value: 'Segurança', label: 'e confiabilidade' }
    ],
    features: [
      { label: 'QR Code', icon: QrCode },
      { label: 'PIX', icon: WalletCards },
      { label: 'Aproximação', icon: CreditCard },
      { label: 'Open Finance', icon: Network },
      { label: 'Carteiras digitais', icon: Smartphone }
    ],
    challenge:
      'Construir uma solução preparada para Open Finance, PIX, cartões por aproximação e carteiras digitais, superando o modelo tradicional de bilhetagem.',
    scale:
      'Projeto desenvolvido para operadores de transporte coletivo que buscam modernizar completamente sua operação.',
    role:
      'Concepção do produto, arquitetura, integração com meios de pagamento e desenvolvimento da plataforma.',
    result:
      'Uma solução preparada para múltiplos meios de pagamento, gestão operacional e evolução contínua da mobilidade urbana.'
  },
  {
    id: 'ecopasse',
    number: '04',
    image: ecopasseImage,
    imageAlt: 'Página real do EcoPasse com o aplicativo de validação',
    name: 'EcoPasse',
    eyebrow: 'Simplicidade operacional sem abrir mão da tecnologia.',
    description:
      'Aplicativo de validação criado para operações de transporte privado que precisam de agilidade, baixo custo e integração.',
    metrics: [
      { value: '7 anos', label: 'em operação contínua' },
      { value: '100 mil+', label: 'condôminos atendidos' },
      { value: 'Milhares', label: 'de acessos mensais' },
      { value: 'QR Code', label: 'validação simples e rápida' }
    ],
    features: [
      { label: 'Leitura de QR Code', icon: QrCode },
      { label: 'Validação rápida', icon: CheckCircle2 },
      { label: 'Operação integrada', icon: Network },
      { label: 'Uso simples', icon: Smartphone }
    ],
    challenge:
      'Disponibilizar uma solução leve e objetiva para operações que necessitam validar passageiros sem adotar uma plataforma complexa.',
    scale:
      'Solução voltada a operadores de transporte privado, condomínios e empresas que precisam controlar o acesso de passageiros.',
    role:
      'Arquitetura da solução, desenvolvimento mobile, integração com o ambiente administrativo e evolução do aplicativo.',
    result:
      'Um aplicativo Android simples de operar, com validação por QR Code e comunicação integrada à plataforma web.'
  }
];

const process = [
  {
    title: 'Entender o negócio',
    text: 'Mergulho no contexto do cliente para compreender o problema real, os processos e os objetivos que precisam ser alcançados.',
    icon: Users
  },
  {
    title: 'Projetar a solução',
    text: 'Concebo a arquitetura, defino tecnologias e desenho uma solução robusta, escalável, segura e preparada para evoluir.',
    icon: Code2
  },
  {
    title: 'Entregar com excelência',
    text: 'Desenvolvo, implanto e acompanho a evolução da solução, garantindo resultados consistentes em produção.',
    icon: Rocket
  }
];

const technologies = [
  { name: 'C#', detail: '.NET', icon: Code2 },
  { name: 'React', detail: 'Frontend', icon: MonitorSmartphone },
  { name: '.NET MAUI', detail: 'Mobile', icon: Smartphone },
  { name: 'SQL Server', detail: 'Dados', icon: Database },
  { name: 'Azure', detail: 'Cloud', icon: Server },
  { name: 'Google Maps', detail: 'Geolocalização', icon: MapPin },
  { name: 'PIX', detail: 'Open Finance', icon: WalletCards }
];

const SectionLabel = ({ children }) => (
  <div className="mb-5 flex items-center justify-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-cyan-400">
    <span className="h-px w-10 bg-cyan-500/40" />
    {children}
    <span className="h-px w-10 bg-cyan-500/40" />
  </div>
);

const CaseDetail = ({ title, text }) => (
  <div className="py-6 lg:px-7 lg:first:pl-0 lg:last:pr-0">
    <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-400">{title}</h4>
    <p className="mt-3 text-sm leading-6 text-slate-300">{text}</p>
  </div>
);

const ProjectVisual = ({ project }) => (
  <figure className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_24px_70px_rgba(0,0,0,.35)]">
    <div className="flex h-9 items-center gap-2 border-b border-slate-200 bg-slate-100 px-4">
      <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
      <span className="ml-3 h-4 flex-1 rounded-full bg-white" />
    </div>
    <img
      src={project.image}
      alt={project.imageAlt}
      className="block h-auto w-full bg-white object-cover"
      loading="lazy"
    />
  </figure>
);

const Wamberto = () => {
  const scrollToSection = (id) => {
    const section = document.getElementById(id);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-[#020817] text-white selection:bg-cyan-400 selection:text-slate-950">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800/80 bg-[#020817]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-8">
          <a href="/" className="text-xl font-bold tracking-tight sm:text-2xl">
            WCOM<span className="text-cyan-400"> Digital</span>
          </a>

          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-400"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para a WCOM
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden pt-32 lg:pt-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_68%_22%,rgba(8,145,178,.24),transparent_32%),radial-gradient(circle_at_25%_30%,rgba(14,116,144,.12),transparent_25%)]" />

          <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
            <div className="grid items-center gap-14 lg:grid-cols-[1.02fr_.98fr] lg:gap-20">
              <div className="pb-16 lg:pb-24">
                <span className="mb-6 inline-flex rounded-full border border-cyan-500/40 bg-cyan-500/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
                  Arquiteto de Software
                </span>

               <h1 className="text-5xl md:text-5xl lg:text-[3.35rem] xl:text-[3.75rem] font-bold leading-none whitespace-nowrap">
    Wamberto de Farias
</h1>

                <p className="mt-6 text-xl font-semibold text-cyan-400 sm:text-2xl">
                  Experiência pessoal. Estrutura empresarial.
                </p>

                <div className="mt-7 max-w-2xl space-y-4 text-base leading-7 text-slate-300 sm:text-lg">
                  <p>
                    Mais de 30 anos concebendo e implementando soluções de software que sustentam operações críticas
                    em empresas e órgãos públicos.
                  </p>
                  <p>
                    Do entendimento do problema à entrega da solução, você conversa diretamente com quem projeta,
                    desenvolve e acompanha cada etapa do projeto.
                  </p>
                </div>

                <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => scrollToSection('contato')}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3.5 font-semibold text-slate-950 transition hover:bg-cyan-300"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Fale sobre o seu projeto
                  </button>

                  <a
                    href="https://wa.me/5521992575060"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/50 px-6 py-3.5 font-semibold text-white transition hover:bg-cyan-400/10"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Chamar no WhatsApp
                  </a>
                </div>
              </div>

              <div className="relative mx-auto w-full max-w-xl self-end">
                <div className="absolute inset-x-10 bottom-0 h-4/5 rounded-full bg-cyan-500/15 blur-3xl" />
                <img
                  src={ceo.image}
                  alt={ceo.name}
                  className="relative z-10 mx-auto max-h-[630px] w-full object-contain object-bottom"
                />

                <div className="absolute bottom-14 right-0 z-20 hidden w-72 rounded-2xl border border-slate-700/80 bg-slate-950/85 p-6 shadow-2xl backdrop-blur-xl sm:block">
                  <div className="mb-3 flex items-center gap-3 font-semibold">
                    <span className="h-2.5 w-2.5 rounded-full bg-cyan-400 shadow-[0_0_14px_rgba(34,211,238,.8)]" />
                    Fundador da WCOM Digital
                  </div>
                  <p className="text-sm leading-6 text-slate-400">
                    Arquiteto de Software especialista em soluções corporativas para empresas e governos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-800/80 bg-[#041022] py-10">
          <div className="mx-auto flex max-w-6xl items-start gap-6 px-6 lg:px-8">
            <div className="text-7xl font-black leading-none text-cyan-500/30">“</div>
            <p className="max-w-5xl text-2xl font-semibold leading-tight text-slate-100 sm:text-3xl lg:text-4xl">
              Ao longo de mais de <span className="text-cyan-400">30 anos</span> participei da concepção de plataformas
              utilizadas diariamente por milhares de pessoas.
            </p>
          </div>
        </section>

        <section id="projetos" className="relative py-24 lg:py-32">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(8,145,178,.08),transparent_42%)]" />

          <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
            <SectionLabel>Projetos que demonstram essa experiência</SectionLabel>
            <div className="mt-12">
              {wamberto.map((project, index) => (
                <article
                  key={project.id}
                  className="border-t border-white/10 py-16 first:border-t-0 first:pt-0 lg:py-24"
                >
                  <div className={`grid items-center gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 ${index % 2 === 1 ? 'lg:grid-cols-[1.22fr_0.78fr]' : ''}`}>
                    <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                      <p className="text-sm font-semibold text-cyan-400">{project.number}</p>
                      <h2 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">{project.name}</h2>
                      <p className="mt-3 text-lg font-semibold text-cyan-300">{project.eyebrow}</p>
                      <p className="mt-5 max-w-xl text-base leading-7 text-slate-300">{project.description}</p>

                      <div className="mt-8 flex flex-wrap gap-x-7 gap-y-5">
                        {project.features.map(({ label, icon: Icon }) => (
                          <div key={label} className="flex items-center gap-2 text-sm text-slate-300">
                            <Icon className="h-4 w-4 text-cyan-400" />
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-9 flex flex-wrap gap-x-10 gap-y-5 border-t border-white/10 pt-7">
                        {project.metrics.map((metric) => (
                          <div key={`${project.id}-${metric.value}`}>
                            <div className="text-2xl font-bold text-white">{metric.value}</div>
                            <div className="mt-1 max-w-36 text-[10px] uppercase tracking-[0.14em] text-slate-500">{metric.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                      <ProjectVisual project={project} />
                    </div>
                  </div>

                  <div className="mt-12 grid border-y border-white/10 divide-y divide-white/10 lg:grid-cols-4 lg:divide-x lg:divide-y-0">
                    <CaseDetail title="O desafio" text={project.challenge} />
                    <CaseDetail title="A escala" text={project.scale} />
                    <CaseDetail title="Minha atuação" text={project.role} />
                    <CaseDetail title="Resultado" text={project.result} />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-800/80 bg-[#041022] py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <SectionLabel>Como eu atuo</SectionLabel>

            <div className="grid overflow-hidden rounded-3xl border border-slate-700/80 bg-[#071426] md:grid-cols-3">
              {process.map(({ title, text, icon: Icon }, index) => (
                <div key={title} className={`p-8 lg:p-10 ${index > 0 ? 'border-t border-slate-700/80 md:border-l md:border-t-0' : ''}`}>
                  <span className="mb-6 grid h-14 w-14 place-items-center rounded-2xl bg-cyan-500/10 text-cyan-400">
                    <Icon className="h-7 w-7" />
                  </span>
                  <h3 className="text-xl font-semibold">{title}</h3>
                  <p className="mt-3 leading-7 text-slate-400">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <SectionLabel>Tecnologias e ferramentas</SectionLabel>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
              {technologies.map(({ name, detail, icon: Icon }) => (
                <div
                  key={name}
                  className="flex min-h-28 flex-col items-center justify-center rounded-2xl border border-slate-700/80 bg-[#071426] p-5 text-center transition hover:-translate-y-1 hover:border-cyan-500/50"
                >
                  <Icon className="h-7 w-7 text-cyan-400" />
                  <div className="mt-3 font-semibold">{name}</div>
                  <div className="mt-1 text-xs text-slate-500">{detail}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contato" className="pb-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-[radial-gradient(circle_at_left,rgba(8,145,178,.2),transparent_35%),#071426] p-8 sm:p-10 lg:p-12">
              <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]">
                <div className="flex items-center gap-6">
                  <img
                    src={ceo.image}
                    alt={ceo.name}
                    className="hidden h-32 w-28 rounded-2xl object-cover object-top sm:block"
                  />
                  <div>
                    <h2 className="text-3xl font-bold sm:text-4xl">Vamos conversar sobre o seu projeto?</h2>
                    <p className="mt-3 text-lg text-slate-400">Você fala diretamente comigo, do início ao fim.</p>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                  <a
                    href="/#contact"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3.5 font-semibold text-slate-950 transition hover:bg-cyan-300"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Fale sobre o seu projeto
                  </a>

                  <a
                    href="https://wa.me/5521992575060"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/50 px-6 py-3.5 font-semibold text-white transition hover:bg-cyan-400/10"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Chamar no WhatsApp
                  </a>
                </div>
              </div>

              <div className="mt-9 grid gap-4 border-t border-slate-700/70 pt-7 sm:grid-cols-3">
                {[
                  'Atendimento direto e personalizado',
                  'Soluções sob medida para o seu negócio',
                  'Compromisso com resultados reais'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm text-slate-300">
                    <CheckCircle2 className="h-5 w-5 flex-none text-cyan-400" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-800 bg-[#020817]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-8 text-sm text-slate-500 sm:flex-row lg:px-8">
          <div className="font-semibold text-white">
            WCOM<span className="text-cyan-400"> Digital</span>
          </div>
          <p>© {new Date().getFullYear()} WCOM Digital. Todos os direitos reservados.</p>
          <p>Rio de Janeiro • Brasil</p>
        </div>
      </footer>
    </div>
  );
};

export default Wamberto;